import React, { useState, useEffect, useMemo } from 'react';
import './ThreeMonthTaskPlanner.css';
import './Buttons.css';
import './Instructions.css';
import './MatrixGrid.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faNoteSticky } from '@fortawesome/free-solid-svg-icons';
import api from '../api';

import ThreeMonthPlannerInstructions from './Utilities/ThreeMonthPlannerInstructions';

const ThreeMonthTaskPlanner = ({ currentGardener, plants, handleTabChange, isDemoMode, currentDate }) => {
    const [events, setEvents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [gardenIcons, setGardenIcons] = useState([]);
    const [showInstructions, setShowInstructions] = useState(false);
    const [activeLabel, setActiveLabel] = useState(null);
    const [cropMonthMatrix, setCropMonthMatrix] = useState({});
    const [cropsWithTasks, setCropsWithTasks] = useState([]);
    const [showIntroModal, setShowIntroModal] = useState(false);

    const monthWindow = useMemo(() => {
        const now = new Date(currentDate);
        const months = [];
        for (let i = 0; i < 3; i++) {
            const monthStart = new Date(now.getFullYear(), now.getMonth() + i, 1);
            const monthEnd = new Date(now.getFullYear(), now.getMonth() + i + 1, 0);
            months.push({
                label: monthStart.toLocaleString('default', { month: 'long' }),
                start: monthStart,
                end: monthEnd
            });
        }
        return months;
    }, []);

    useEffect(() => {
        if (currentGardener?.isNewUser) {
            setShowIntroModal(true);
        }
    }, [currentGardener]);

    useEffect(() => {
        const fetchEvents = async () => {
            try {
                const response = await api.get(`/tasks/threeMonthTasks/${currentGardener?.id}`, {
                    params: {
                        scheduleTable: currentGardener.scheduleTable
                    }
                });

                const data = response.data;
                setEvents(data);
            } catch (err) {
            console.error('Error fetching events:', err);
            } finally {
            setLoading(false);
            }
        };

        const fetchGardenIcons = async () => {
            try {
            const response = await api.get('/gardens/icons');
            const data = response.data;
            setGardenIcons(data);
            } catch (err) {
            console.error('Error fetching garden icons:', err);
            }
        };
        fetchEvents();
        fetchGardenIcons();
    }, []);

    useEffect(() => {
        if (!events.length || !plants.length) return;

        const cropNames = Array.from(new Set(plants.map(p => p.name)));
        const matrix = {};
        cropNames.forEach(crop => {
            matrix[crop] = {};
            monthWindow.forEach(month => {
                matrix[crop][month.label] = [];
            });
        });

        events.forEach((event, index) => {
            const plant = plants.find(p => Number(p.id) === Number(event.plantID));
            if (!plant || !event.date ) return;

            const cropName = plant.name;
            const recommended = getRecommendedQuantity(plant);
            const taskType = event.eventType || 'Task';

            let methodText;
            if (taskType.toLowerCase() === 'planting') {
                if (event.succession) {
                    // If succession label exists, show it
                    methodText = event.succession;
                } else {
                    // Otherwise, show method + quantity as before
                    const parts = [];
                    if (event.plantingMethod) parts.push(event.plantingMethod);
                    if (recommended) parts.push(recommended);
                    methodText = parts.join(' - ');
                }
             } else {
                // For everything else, just show the task type
                methodText = taskType;
            }
            const isoParts = event.date.split('T')[0].split('-');
            const eventDate = new Date(
                Number(isoParts[0]),
                Number(isoParts[1]) - 1,
                Number(isoParts[2])
            );

            const eventMonthLabel = eventDate.toLocaleString('default', { month: 'long' });
            const eventYear = eventDate.getFullYear();

            monthWindow.forEach(month => {
                const monthLabel = month.label;
                const monthYear = month.start.getFullYear();

                if (eventMonthLabel === monthLabel && eventYear === monthYear) {
                    const monthTasks = matrix[cropName][monthLabel];
                    if (!monthTasks.includes(methodText)) {
                        monthTasks.push(methodText);
                    }

                }
            });
        });

        const filteredCrops = cropNames.filter(crop =>
            monthWindow.some(month => matrix[crop][month.label]?.length > 0)
        );

        setCropMonthMatrix(matrix);
        setCropsWithTasks(filteredCrops);
    }, [events, plants, monthWindow]);

    const getRecommendedQuantity = (plant) => {
        if (!plant || !plant.amount || !plant.amounttype || !currentGardener?.familysize) return null;
        const total = plant.amount * currentGardener.familysize;
        return `${total} ${plant.amounttype}`;
    };

    const getPlantIconFromPlantID = (plantID) => {
        const plant = plants.find(p => Number(p.id) === Number(plantID));
        if (!plant || !plant.icon) return null;
        return (
            <img
                src={`data:image/png;base64,${plant.icon}`}
                alt="Crop"
                title={plant.name}
                className="plant-icon"
            />
        );
    };

    const getGardenIconFromPlantID = (plantID) => {
        const plant = plants.find(p => Number(p.id) === Number(plantID));
        if (!plant || !plant.gardentype) return null;
        const match = gardenIcons.find(g => Number(g.gardenID) === Number(plant.gardentype));
        if (!match) return null;
        return (
            <img
                src={`data:image/png;base64,${match.icon}`}
                alt="Garden Type"
                title={match.hint}
                className="garden-icon"
            />
        );
    };

    const getIconsForCrop = (cropName) => {
        const plant = plants.find(p => p.name === cropName);
        if (!plant) return { plantIcon: null, gardenIcon: null };
        return {
            plantIcon: getPlantIconFromPlantID(plant.id),
            gardenIcon: getGardenIconFromPlantID(plant.id)
        };
    };

    const experienceLevel = currentGardener.experienceLevel || 'Beginner';
    const handleRowClick = (label) => {
        setActiveLabel(prev => (prev === label ? null : label));
    };

    if (loading || !currentGardener) {
        return (
            <div className="add-gardener-form-container">
                <div className="add-gardener-form">
                    <h2>Loading your seasonal planner...</h2>
                    <pre style={{ background: '#eee', padding: '1rem' }}>
                        {JSON.stringify({ loading, currentGardener, events }, null, 2)}
                    </pre>
                </div>
            </div>
        );
    }
    return (
      <section className="seasonal-planner-wrapper">
        {showIntroModal && (
            <div className="seasonal-intro-highlight">
<p>
  <strong>Welcome to Your Seasonal Planner</strong><br />
  See what is coming next for your garden. Based on the your crops and location, the planner maps out the next three months - showing when each crop will be planted, sprout, thinned, and harvested.
</p>

<p>
  <strong>Sunlight Icons:</strong><br />
  Each crop includes its ideal sunlight conditions, helping you plan your growing space.
</p>

<p>
  <strong>Family-Size Guidance:</strong><br />
  Recommendations are tailored to your family size. This ensures you are growing enough to feed your family - without overplanting or underestimating your harvest needs.
</p>

<p>
  <strong>Plan with Confidence:</strong><br />
  Use the planner to anticipate what is coming, prepare in advance, and stay on track. It is your roadmap for the season - guiding you step by step so you are never caught off guard.
</p>
                    <button
                        className="button"
                        onClick={() => {
                            setShowIntroModal(false);
                            handleTabChange('Home',true);
                        }}
                    >
                        Next...
                    </button>
            </div>
        )}

        <div className="sp-banner-greeting">
            <h2>{experienceLevel} Seasonal Planner</h2>
            <p className="sp-planner-subheading">
                This planner helps you visualize your season. You will be notified when actual planting tasks are triggered by weather and soil conditions.
            </p>
            <button
                    type="button"
                    className="button"
                    onClick={() => setShowInstructions(prev => !prev)}
                    aria-expanded={showInstructions}
                    aria-controls="planner-instructions"
            >
                    {showInstructions ? 'Hide Help' : 'Show Help'}
            </button>

            {showInstructions && (
                <div className="instructions-panel">
                            <ThreeMonthPlannerInstructions />
                </div>
            )}
        </div>
                <div className="matrix-scroll-wrapper">
                    <div className="matrix-grid">
                        <div className="matrix-columns sp-header-row">
                        <div className="matrix-column sp-crop-column sp-highlight matrix-sticky-column">Crop</div>
                            {monthWindow.map((month, index) => (
                                    <div
                                        key={month.label}
                                    className={`matrix-column sp-month-column sp-highlight ${index === 0 ? 'sp-visible-mobile' : ''}`}
                                    >
                                        <h3 className="sp-section-title">{month.label}</h3>
                                    </div>
                            ))}
                        </div>

                    {cropsWithTasks.length > 0 ? (
                        cropsWithTasks.map(crop => {
                                const plant = plants.find(p => p.name === crop);
                                if (!plant) return null;
                                const recommended = getRecommendedQuantity(plant);
                                const { plantIcon, gardenIcon } = getIconsForCrop(crop);
                                const cropEvents = events.filter(e => Number(e.plantID) === Number(plant.id));

                            return (
                                <React.Fragment key={crop}>
                                    <div className="matrix-columns sp-data-row">
                                        <div className="matrix-column sp-crop-column sp-highlight matrix-sticky-column">
                                            <button
                                                className="crop-label-button"
                                                onClick={() => handleRowClick(crop)}
                                                aria-expanded={activeLabel === crop}
                                                aria-controls={`crop-card-${crop}`}
                                            >
                                                <span className="crop-label">
                                                    {gardenIcon && <span className="garden-icon">{gardenIcon}</span>}
                                                    {plantIcon && <span className="plant-icon">{plantIcon}</span>}
                                                    <span>{crop}</span>
                                                </span>
                                            </button>
                                        </div>

                                        {monthWindow.map((month, index) => {
                                            const methods = cropMonthMatrix[crop]?.[month.label] || [];
                                            return (
                                                <div
                                                    key={month.label}
                                                    className={`matrix-column sp-month-column sp-highlight ${index === 0 ? 'sp-visible-mobile' : ''}`}
                                                >
                                                    {methods.length > 0 ? (
                                                        <ul className="planting-method-list">
                                                            {methods.map((method, idx) => {
                                                                const typeMatch = method.match(/^(Planting|Thinning|Harvesting|Germinating)/i);
                                                                const typeClass = typeMatch ? `task-type-${typeMatch[0].toLowerCase()}` : '';
                                                                return (
                                                                    <li key={idx} className={typeClass}>{method}</li>
                                                                );
                                                            })}
                                                        </ul>
                                                    ) : (
                                                        <p className="no-tasks">No tasks</p>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>

                                    {activeLabel === crop && (
                                        <div
                                            className="sp-data-row crop-card-row"
                                            id={`crop-card-${crop}`}
                                            role="region"
                                            aria-label={`Details for ${crop}`}
                                        >
                                            <div className="crop-card sp-highlight">
                                                <h4>{crop} Details</h4>

                                                {Array.from(new Set(
                                                    cropEvents.filter(e => e.variety).map(e => e.variety)
                                                )).length > 0 && (
                                                    <div className="variety-list">
                                                        <p><strong>Varieties:</strong></p>
                                                        <ul>
                                                            {Array.from(new Set(
                                                                cropEvents.filter(e => e.variety).map(e => e.variety)
                                                            )).map((variety, idx) => (
                                                                <li key={idx}>{variety}</li>
                                                            ))}
                                                        </ul>
                                                    </div>
                                                )}

                                                {plant.companioninfo && (
                                                    <p><strong>Companion Plants:</strong> {plant.companioninfo}</p>
                                                )}
                                                {plant.notes && (
                                                    <p><strong>Notes:</strong> {plant.notes}</p>
                                                )}
                                                {recommended && (
                                                    <p><strong>Recommended Quantity:</strong> {recommended}</p>
                                                )}
                                            </div>
                                        </div>
                                    )}
                                </React.Fragment>
                            );
                        })
                    ) : (
                        <div className="no-tasks-message" style={{ textAlign: 'center', padding: '2rem', fontSize: '1.2rem', color: '#555' }}>
                            There are no crops with tasks in the next three months.<br />
                                Check <strong>Jordan the All-Year Gardener</strong> to see which crops have upcoming tasks.
                        </div>
                    )}
                    </div>
                </div>
      </section>
    );
};

export default ThreeMonthTaskPlanner;
